"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { IconClose } from "@/app/components/atoms/Icons";
import { IconButton } from "@/app/components/atoms/IconButton";
import { cx } from "@/app/lib/utils";
import styles from "./SideSheet.module.scss";
import {
  CLOSE_ICON_SIZE,
  CLOSE_LABEL,
  EXIT_FALLBACK_MS,
  FOCUSABLE_SELECTOR,
} from "./SideSheet.constants";
import type { SideSheetProps } from "./SideSheet.types";

/**
 * SideSheet — DS Entradas
 * Panel off-canvas que entra desde el borde derecho sobre un scrim modal.
 *
 * Contrato modal (a11y): `role="dialog"` + `aria-modal="true"` + nombre
 * accesible (`aria-labelledby` con el título, o `aria-label`). Al abrir, el
 * foco entra en el panel y, al cerrar, vuelve al elemento que lo abrió. Escape
 * y el clic en el scrim cierran. El scroll del body se bloquea mientras está
 * abierto. Se renderiza por portal en `document.body` y respeta reduced-motion.
 *
 * Al cerrar, el panel no se desmonta en seco: sale hacia el mismo borde por el
 * que entró (y el scrim se desvanece) y se desmonta al terminar la animación.
 * Durante la salida queda `inert` + `aria-hidden`: ya no es interactivo ni
 * forma parte del árbol accesible.
 *
 * Componente controlado: `open` / `onClose`.
 */
export function SideSheet({
  open,
  onClose,
  children,
  title,
  ariaLabel,
  footer,
  size = "l",
  variant = "default",
  titleSize = "m",
  className,
}: SideSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // `rendered` sigue a `open`, salvo al cerrar: se mantiene en `true` mientras
  // dura la animación de salida (`closing`). Al abrir se sincroniza durante el
  // render (sin efecto) para que el panel aparezca en el mismo paso.
  const [rendered, setRendered] = useState(open);
  if (open && !rendered) setRendered(true);
  const closing = rendered && !open;

  // Fin de la salida: `animationend` del panel o, si no llega (movimiento
  // reducido, pestaña en segundo plano…), la pausa de seguridad.
  const finishExit = useCallback(() => setRendered(false), []);
  useEffect(() => {
    if (!closing) return;
    const reducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = setTimeout(finishExit, reducedMotion ? 0 : EXIT_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [closing, finishExit]);

  // Bloqueo de scroll de la página mientras el panel está abierto.
  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = previous;
    };
  }, [open]);

  // Captura del trigger al abrir y restauración del foco al cerrar.
  // El portal se renderiza en el mismo paso cuando `open` es true, así que
  // `panelRef` ya existe cuando corre este efecto.
  useEffect(() => {
    if (open) {
      // Guarda el disparador solo si aún no estaba registrado para esta apertura.
      triggerRef.current ??= document.activeElement as HTMLElement | null;
      // Mueve el foco DENTRO del panel: primer enfocable o el propio panel.
      const panel = panelRef.current;
      if (panel && !panel.contains(document.activeElement)) {
        const focusable = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (focusable ?? panel).focus();
      }
      return;
    }
    // Al cerrar, devuelve el foco al disparador original si sigue en el DOM.
    const trigger = triggerRef.current;
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    }
    triggerRef.current = null;
  }, [open]);

  // Cierre con Escape (a nivel de documento para no depender del foco interno).
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Focus-trap: Tab cíclico entre el primer y el último enfocable del panel.
  const handlePanelKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;

      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [],
  );

  if (!rendered || typeof document === "undefined") return null;

  const labelledBy = title ? titleId : undefined;
  const accessibleName = title ? undefined : ariaLabel;

  return createPortal(
    <div
      className={styles.root}
      data-closing={closing ? "" : undefined}
      inert={closing}
      aria-hidden={closing || undefined}
    >
      <div className={styles.scrim} aria-hidden="true" onClick={onClose} />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={accessibleName}
        tabIndex={-1}
        className={cx(
          styles.panel,
          styles[`panel--${size}`],
          variant === "plain" && styles["panel--plain"],
          className,
        )}
        onKeyDown={handlePanelKeyDown}
        onAnimationEnd={(event) => {
          if (closing && event.target === event.currentTarget) finishExit();
        }}
      >
        <header className={styles.header}>
          {title && (
            <h2
              id={titleId}
              className={cx(styles.title, titleSize === "l" && styles["title--l"])}
            >
              {title}
            </h2>
          )}
          <IconButton
            icon={<IconClose size={CLOSE_ICON_SIZE} />}
            ariaLabel={CLOSE_LABEL}
            variant="ghost"
            size="m"
            onClick={onClose}
            className={styles.close}
          />
        </header>

        <div className={styles.content}>{children}</div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
